import { AlertV2, ButtonV2, Spinner } from '@cimplico/components';
import { CloudArrowDownIcon } from '@heroicons/react/20/solid';
import Dropzone from '../../../../components/dropzone/Dropzone';
import { ReactComponent as UploadIcon } from '../../../../assets/upload.svg';
import FileInput from '../../../files/FileInput';
import { useRef, useState } from 'react';
import { useSyncEntityChart } from '../../../../queries/use-sync-entity-chart';
import { parseAsync } from './utils';
import { validateCsv } from './validation/manual-import.schema';
import ImportErrors from './ImportErrors';
import { COASyncReport } from '../../../../types/entity';

const template = ['*Code', '*Name', 'Description', '*Report Code', '*Type', '*Class'];

type ManualImportProps = {
    entityId: string;
    chartId: string;
    isReplacing: boolean;
    onImport: (results: COASyncReport) => void;
};
function ManualImport({ chartId, entityId, isReplacing, onImport }: ManualImportProps): JSX.Element {
    const syncQuery = useSyncEntityChart(entityId);
    const uploadRef = useRef<HTMLInputElement | null>(null);
    const [error, setError] = useState<{ message: string; details?: { line: number; errors: { field: string; message: string }[] }[] }>();
    const [viewErrorDetails, setViewErrorDetails] = useState(false);

    /**
     * "Clicks" the hidden file input element allowing the user to select a file to upload.
     */
    function openFileInput(): void {
        if (uploadRef.current) {
            uploadRef.current.click();
        }
    }

    /**
     * Generates a blank chart csv file and immediately downloads it.
     */
    function downloadTemplate(): void {
        const file = new Blob([template.join(',')], { type: 'text/csv' });
        const element = document.createElement('a');
        element.href = URL.createObjectURL(file);
        element.download = 'chart-of-accounts-template.csv';
        document.body.appendChild(element);
        element.click();
    }

    /**
     * Handles the chart of accounts csv uploaded by the user
     * @param files
     */
    async function handleUpload(files: File[]): Promise<void> {
        const invalidFormatError = 'Invalid template - please ensure all template columns are present and there is at least 1 account.';

        const file = files[0];
        if (file.type !== 'text/csv') {
            setError({ message: 'Invalid file type - please upload a valid CSV file.' });
            return;
        }

        try {
            const parsedFile = await parseAsync(file);

            // Check the file is an array of row objects
            if (!Array.isArray(parsedFile) || typeof parsedFile[0] !== 'object') {
                setError({ message: invalidFormatError });
                return;
            }

            const columns = Object.keys(parsedFile[0]);
            // Ensure the upload csv has at least the required columns
            if (!columns || !['code', 'name', 'reportCode', 'type', 'class'].every((header) => columns.includes(header))) {
                setError({ message: invalidFormatError });
                return;
            }

            const res = validateCsv(parsedFile);

            if (!res.success) {
                setError({
                    message: 'The upload template contains invalid rows - click to see more details.',
                    details: res.error.issues,
                });
                return;
            }

            if (res.success && res.data.length > 0) {
                syncQuery.mutate(
                    { chartId, accounts: res.data },
                    {
                        onSuccess: (res) => {
                            onImport(res.results);
                        },
                        onError: (err) => {
                            if (err.response?.data && 'message' in err.response?.data) {
                                setError({ message: err.response?.data.message });
                            } else {
                                setError({ message: 'An unknown error occurred - please try again.' });
                            }
                        },
                    }
                );
            }
        } catch (error) {
            setError({ message: 'An unknown error occurred - please try again.' });
        }
    }

    return (
        <>
            {viewErrorDetails && <ImportErrors onClose={(): void => setViewErrorDetails(false)} errors={error?.details ?? []} />}
            {error && (
                <AlertV2 title="Unable to import chart." type="error" description={error.message} onClose={(): void => setError(undefined)}>
                    {error.details && (
                        <AlertV2.Action
                            label="View details"
                            onClick={(): void => {
                                setViewErrorDetails(true);
                            }}
                        />
                    )}
                </AlertV2>
            )}
            <div className="flex flex-col justify-center items-center gap-2 h-full">
                <FileInput innerRef={uploadRef} onSelect={handleUpload} />
                {!isReplacing && (
                    <>
                        <p className="text-gray-700 text-sm font-bold mb-2">
                            Upload your chart of accounts using our template to get started.
                        </p>
                        <ButtonV2
                            label="Download template"
                            type="secondary"
                            size="sm"
                            leftIcon={<CloudArrowDownIcon />}
                            onClick={downloadTemplate}
                        />
                        <p className="text-sm text-gray-600">then</p>
                    </>
                )}
                <div className="w-full h-full">
                    {syncQuery.isLoading ? (
                        <div className="w-full h-full flex items-center justify-center pt-4">
                            <Spinner />
                        </div>
                    ) : (
                        <Dropzone onDrop={handleUpload} className="rounded-md">
                            <div
                                className="flex flex-col items-center mt-2 justify-center border-dashed border border-gray-300 rounded-md p-4 cursor-pointer w-full h-full"
                                onClick={openFileInput}
                            >
                                <UploadIcon />
                                <p className="mt-2 text-sm">Upload filled-in template (.csv)</p>
                            </div>
                        </Dropzone>
                    )}
                </div>
            </div>
        </>
    );
}

export default ManualImport;
